import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { DatePicker } from '@atlaskit/datetime-picker';
import ErrorIcon from '@atlaskit/icon/utility/migration/error';
import Popup from '@atlaskit/popup';
import { R400 } from '@atlaskit/theme/colors';
import { layers } from '@atlaskit/theme/constants';
import { useDatasourceAnalyticsEvents } from '../../../../../analytics';
import { dateRangeMessages } from './messages';
import { PopupComponent } from './PopupComponent';
import { CustomDateWrapper, CustomDropdown, CustomDropdownItem, DatePickersWrapper, DateRangeErrorMessage, SelectDateRangeButton } from './styled';
import { PopupTrigger } from './trigger';
import { getCurrentOptionLabel, getDropdownLabel, useInvalidDateRange } from './utils';
var dateRangeValues = ['anyTime', 'today', 'yesterday', 'past7Days', 'past30Days', 'pastYear', 'custom'];
var defaultOptionValue = 'anyTime';
export var DateRangePicker = function DateRangePicker(_ref) {
  var onSelectionChange = _ref.onSelectionChange,
    selection = _ref.selection,
    _ref$filterName = _ref.filterName,
    filterName = _ref$filterName === void 0 ? 'datasource-date-range-picker' : _ref$filterName;
  var _ref2 = selection || {},
    selectedValue = _ref2.value,
    fromDate = _ref2.from,
    toDate = _ref2.to;
  var todayDate = new Date().toISOString();
  var _useIntl = useIntl(),
    locale = _useIntl.locale,
    formatMessage = _useIntl.formatMessage,
    formatDate = _useIntl.formatDate;
  var _useDatasourceAnalyti = useDatasourceAnalyticsEvents(),
    fireEvent = _useDatasourceAnalyti.fireEvent;
  var _useState = useState(selectedValue),
    _useState2 = _slicedToArray(_useState, 2),
    currentOption = _useState2[0],
    setCurrentOption = _useState2[1];
  var _useState3 = useState(fromDate),
    _useState4 = _slicedToArray(_useState3, 2),
    customFromDate = _useState4[0],
    setCustomFromDate = _useState4[1];
  var _useState5 = useState(toDate),
    _useState6 = _slicedToArray(_useState5, 2),
    customToDate = _useState6[0],
    setCustomToDate = _useState6[1];
  var _useState7 = useState(undefined),
    _useState8 = _slicedToArray(_useState7, 2),
    isPickerOpen = _useState8[0],
    setIsPickerOpen = _useState8[1];
  var isCustomSelected = currentOption === 'custom';
  var analyticsPayload = useMemo(function () {
    return {
      filterName: filterName,
      selectionCount: (currentOption !== null && currentOption !== void 0 ? currentOption : defaultOptionValue) === defaultOptionValue ? 0 : 1
    };
  }, [filterName, currentOption]);
  var invalidDateRange = useInvalidDateRange(customFromDate, customToDate);
  var handleClickUpdateDateRange = function handleClickUpdateDateRange() {
    onSelectionChange({
      value: 'custom',
      from: customFromDate,
      to: customToDate
    });
    setIsPickerOpen(false);
  };
  var handleClickFilterOption = useCallback(function (option) {
    if (option === currentOption) {
      setIsPickerOpen(false);
      return;
    }
    setCurrentOption(option);
    onSelectionChange({
      value: option
    });
    if (option !== 'custom') {
      setIsPickerOpen(false);
    }
  }, [onSelectionChange, currentOption]);
  var handlePickerToggle = function handlePickerToggle() {
    if (isPickerOpen) {
      setCustomToDate(toDate);
      setCustomFromDate(fromDate);
      setIsPickerOpen(false);
      return;
    }
    setIsPickerOpen(true);
    fireEvent('ui.dropdown.opened.basicSearchDropdown', analyticsPayload);
  };
  useEffect(function () {
    if (isPickerOpen === false) {
      fireEvent('ui.dropdown.closed.basicSearchDropdown', analyticsPayload);
    }
  }, [analyticsPayload, fireEvent, isPickerOpen]);
  return /*#__PURE__*/React.createElement(Popup, {
    isOpen: !!isPickerOpen,
    onClose: handlePickerToggle
    // @ts-ignore: [PIT-1685] Fails in post-office due to backwards incompatibility issue with React 18
    ,
    popupComponent: PopupComponent,
    zIndex: layers.modal(),
    content: function content() {
      return /*#__PURE__*/React.createElement(CustomDropdown, null, dateRangeValues.map(function (option) {
        return /*#__PURE__*/React.createElement(CustomDropdownItem, {
          key: option
          // want to show Anytime as selected if none of the other options are selected
          ,
          isSelected: selectedValue ? option === selectedValue : option === defaultOptionValue,
          onClick: function onClick() {
            return handleClickFilterOption(option);
          }
        }, getDropdownLabel(option, formatMessage));
      }), isPickerOpen && isCustomSelected && /*#__PURE__*/React.createElement(CustomDateWrapper, null, /*#__PURE__*/React.createElement(DatePickersWrapper, null, /*#__PURE__*/React.createElement(DatePicker, {
        maxDate: todayDate,
        innerProps: {
          style: {
            width: 140
          }
        },
        testId: "date-from-picker",
        dateFormat: "D MMM YYYY",
        onChange: setCustomFromDate,
        defaultValue: fromDate,
        placeholder: formatMessage(dateRangeMessages.dateRangeFrom),
        isInvalid: Boolean(invalidDateRange),
        locale: locale,
        selectProps: {
          styles: {
            placeholder: function placeholder(base) {
              return _objectSpread(_objectSpread({}, base), {}, {
                width: 'max-content'
              });
            }
          }
        }
      }), /*#__PURE__*/React.createElement(FormattedMessage, dateRangeMessages.dateRangeToLabel), /*#__PURE__*/React.createElement(DatePicker, {
        maxDate: todayDate,
        innerProps: {
          style: {
            width: 140
          }
        },
        testId: "date-to-picker",
        dateFormat: "D MMM YYYY",
        onChange: setCustomToDate,
        defaultValue: toDate,
        placeholder: formatMessage(dateRangeMessages.dateRangeTo),
        isInvalid: Boolean(invalidDateRange),
        locale: locale,
        selectProps: {
          styles: {
            placeholder: function placeholder(base) {
              return _objectSpread(_objectSpread({}, base), {}, {
                width: 'max-content'
              });
            }
          }
        }
      })), invalidDateRange && /*#__PURE__*/React.createElement(DateRangeErrorMessage, null, /*#__PURE__*/React.createElement(ErrorIcon, {
        LEGACY_size: "small",
        color: "var(--ds-icon-danger, ".concat(R400, ")"),
        label: "Date range error",
        LEGACY_margin: "0 ".concat("var(--ds-space-negative-025, -2px)")
      }), invalidDateRange), /*#__PURE__*/React.createElement(SelectDateRangeButton, {
        "data-testId": "custom-date-range-update-button",
        disabled: Boolean(invalidDateRange),
        onClick: handleClickUpdateDateRange
      }, formatMessage(dateRangeMessages.dateRangeUpdateButton))));
    },
    placement: "bottom-start",
    trigger: function trigger(triggerProps) {
      var labelText = getCurrentOptionLabel(formatDate, formatMessage, selectedValue, toDate, fromDate);
      var isSelected = !!selectedValue || !!isPickerOpen;
      return /*#__PURE__*/React.createElement(PopupTrigger, {
        triggerProps: triggerProps,
        isSelected: isSelected,
        labelPrefix: formatMessage(dateRangeMessages.dateRangeTitle),
        selectedLabel: labelText,
        onClick: handlePickerToggle
      });
    }
  });
};